/* iceberg-ui */
import { Icon, IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';

/*  Shared  */
import { StringAvatar } from 'application/Shared/Components/StringAvatar';
import { DarkTooltip, TooltipQ } from 'application/Shared/Components/Tooltips';
import { TruncatedText } from 'application/Shared/Components/TruncatedText';
import { FontSizes } from 'application/Shared/Constants';
import { Response, Route } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/* Hooks */
import { useFetchSingleUser } from 'hooks/dataAccess/User/user';

/*  React  */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

/**
 * React Context for sharing user data throughout the application.
 * Provides access to the current user object and related functionality.
 *
 * @type {React.Context<{user: UserType|null}>}
 *
 * @property {UserType|null} user - The current user object or null if no user is set
 */
const UserContext = React.createContext({ user: null });

export const useUserContext = () => {
    const context = useContext(UserContext);

    if (context === undefined) {
        throw new Error(
            '`useUserContext` must be used within `UserContextProvider`'
        );
    }

    return context;
};

const loadUser = async ({
    clientId,
    errorFn,
    id,
    includeClients = true,
    setUser
}) => {
    // Setting includeClients to true because of our reports use case, running admin reports at the partner level.
    // In this case, the platformTenantId is the partner's and we need to find all the users that are at the client level.
    const params = { includeClients };

    if (clientId) {
        params.platformTenantId = clientId;
        params.includeClients = true; // this parameter may be redundant since we set platformTenantId as a search parameter
    }
    if (includeClients) {
        params.includeClients = true;
        params.platformTenantId = null;
    }
    const { CancelToken } = axios;
    const source = CancelToken.source();

    try {
        const response = await axios.get(`/user/${id}`, {
            cancelToken: source.token,
            params
        });
        if (!Response.hasData(response)) {
            throw new Error(response?.data?.message);
        } else {
            setUser(Response.getOne(response));
        }
    } catch (e) {
        debug.e(e);
        if (errorFn) {
            errorFn('There was an issue loading the user information', true);
        }
    }
};

const load = async ({
    clientId,
    data,
    errorFn,
    id,
    includeClients,
    setUser
}) => {
    if (id) {
        await loadUser({ clientId, errorFn, id, includeClients, setUser });
        return;
    }

    if (data) {
        setUser(data);
    }

    if (!data && !id) {
        setUser({ name: 'no_id_or_data' });
    }
};

const defaultErrorFn = () => {};

/**
 * A provider component that loads and manages user data in context.
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components that will have access to the user context
 * @param {User|UserSelf} [props.data] - User data object to initialize with
 * @param {Function} [props.errorFn=defaultErrorFn] - Error handler function called when user loading fails
 * @param {string} [props.id] - User ID to load data for
 * @param {boolean} [props.includeClients] - Whether to include client data when loading user
 *
 * @returns {React.ReactElement} A UserContext.Provider wrapping the children
 *
 * @example
 * <User id="123" includeClients>
 *   <ChildComponent />
 * </User>
 */
export const User = ({
    children,
    data,
    errorFn = defaultErrorFn,
    id,
    includeClients
}) => {
    const { clientId } = useParams();
    const [loaded, setLoaded] = useState(false);
    const [user, setUser] = useState(data);

    useEffect(() => {
        setLoaded(false);

        async function fetchData() {
            await load({
                clientId,
                data,
                errorFn,
                id,
                includeClients,
                setUser
            });
            setLoaded(true);
        }

        fetchData();
    }, [clientId, data, errorFn, id, includeClients]);

    const userMemo = useMemo(() => ({ user }), [user]);

    return (
        loaded && (
            <UserContext.Provider value={userMemo}>
                {children}
            </UserContext.Provider>
        )
    );
};

/**
 * Gets the full name of a user based on their first name, last name and email.
 *
 * @param {Object} userDetails - The user object
 * @param {string} userDetails.firstName - User's first name
 * @param {string} userDetails.lastName - User's last name
 * @param {string} userDetails.email - User's email address
 * @returns {string} The formatted full name based on available fields:
 *  - Returns email if anonymous is true
 *  - Returns "firstName lastName" if both exist and firstName is not "Anonymous"
 *  - Returns "firstName" if only firstName exists and is not "Anonymous"
 *  - Returns "lastName" if only lastName exists or firstName is "Anonymous"
 *  - Returns email as fallback if no valid name fields exist
 */
export const getFullName = (user) => {
    const { firstName, lastName, email, anonymous } = user;

    if (anonymous) {
        return email;
    }

    let fullName;

    if (!!firstName && firstName !== 'Anonymous') {
        if (lastName) {
            fullName = `${firstName} ${lastName}`;
        } else {
            fullName = firstName;
        }
    } else if (
        (firstName === 'Anonymous' && !!lastName) ||
        (!firstName && !!lastName)
    ) {
        fullName = lastName;
    } else {
        fullName = email;
    }

    return fullName;
};

const ConditionalUserLink = ({
    children,
    className,
    'data-trackid': dataTrackid,
    link = false,
    userOverride
}) => {
    const { clientId, partnerId } = useParams();
    const route = Route.buildBaseAdminRoute({ clientId, partnerId });
    const { user: userContext } = useContext(UserContext);
    const user = userOverride || userContext;

    if (!user) {
        return null;
    }

    const { id } = user;

    return link ? (
        <Link
            className={className}
            to={`${route}/user/${id}/overview`}
            data-trackid={dataTrackid}
        >
            {children}
        </Link>
    ) : (
        children
    );
};

/**
 * A component that displays a user's full name with optional truncation.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.className] - CSS class name to apply to the component
 * @param {boolean} [props.isTooltipText] - If true, uses smaller font size for tooltip text
 * @param {number} [props.lines] - Number of lines to truncate text to
 * @param {boolean} [props.link] - Link to user's overview page.
 * @param {boolean} [props.noTruncate] - If true, disables text truncation
 * @param {Object} [props.style] - Custom styles to apply to the component
 * @param {Object} [props.userOverride] - User object to override the user from context
 * @param {boolean} [props.useWhiteFont] - If true, uses white text color
 *
 * @returns {React.ReactElement} A component displaying the user's full name
 *
 * @example
 * <UserFullName
 *   lines={2}
 *   userOverride={{
 *     firstName: "John",
 *     lastName: "Doe",
 *     email: "john.doe@example.com"
 *   }}
 * />
 */
export const UserFullName = ({
    className,
    isTooltipText,
    lines,
    link = false,
    noTruncate,
    style,
    userOverride,
    useWhiteFont
}) => {
    const { user: userContext } = useContext(UserContext);
    const user = userOverride || userContext;
    const { t } = useTranslation();

    if (user == null) {
        return null;
    }

    const { anonymous } = user;

    const fullName = getFullName(user);

    if (!noTruncate) {
        return (
            <Box display="flex" alignItems="center" width="100%">
                <Box
                    display="flex"
                    flex={1}
                    sx={{
                        color: useWhiteFont ? 'text.white' : 'text.primary',
                        width: '100%'
                    }}
                >
                    <ConditionalUserLink
                        data-trackid="UserFullNameClassLinkRender"
                        link={link}
                        userOverride={userOverride}
                    >
                        <TruncatedText
                            className={className}
                            component="span"
                            key={`userFullNameComponent_${user.id}`}
                            maxLines={lines}
                            style={{
                                display: 'flex',
                                fontSize: isTooltipText
                                    ? FontSizes[0]
                                    : FontSizes[1],
                                width: '100%',
                                ...style
                            }}
                            containerClassName={link ? 'link' : null}
                            text={fullName}
                            data-testid="userFullNameComponentTruncate"
                            showTooltip={false}
                        />
                    </ConditionalUserLink>
                </Box>
                {anonymous && <TooltipQ text={t('Tooltips.domain_managed')} />}
            </Box>
        );
    }

    return (
        <Box
            className={className}
            display="flex"
            alignItems="center"
            sx={{
                color: useWhiteFont ? 'text.white' : 'text.primary',
                display: 'flex',
                fontSize: isTooltipText ? FontSizes[0] : FontSizes[1],
                overflowWrap: 'anywhere',
                width: '100%',
                ...style
            }}
            data-testid="userFullNameComponentNoTruncate"
        >
            <ConditionalUserLink
                className="link underline"
                data-trackid="UserFullNameClassLinkRender"
                link={link}
                userOverride={userOverride}
            >
                {fullName}
            </ConditionalUserLink>
            {anonymous && <TooltipQ text={t('Tooltips.domain_managed')} />}
        </Box>
    );
};

/**
 * A component that displays a user's email address with optional icon-only view.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.className=''] - CSS class name to apply to the component
 * @param {boolean} [props.iconOnly=false] - If true, displays only an email icon button instead of the full email address
 * @param {boolean} [props.noTooltip=false] - If true, disables the tooltip when in icon-only mode
 * @param {boolean} [props.useWhiteFont=false] - If true, uses white text color instead of default
 *
 * @returns {React.ReactElement} A component displaying the user's email address or email icon
 *
 * @example
 * <UserEmail
 *   iconOnly={true}
 *   noTooltip={false}
 *   useWhiteFont={true}
 * />
 */
export const UserEmail = ({
    className = '',
    iconOnly = false,
    noTooltip = false,
    useWhiteFont = false
}) => {
    const { t } = useTranslation();
    const { user } = useContext(UserContext);

    if (user?.anonymous || !user?.email) {
        return (
            <Typography
                data-testid="noUserEmail"
                className={className}
                sx={{
                    color: useWhiteFont ? 'text.white' : 'text.primary'
                }}
            >
                {t('Common.noEmailFound')}
            </Typography>
        );
    }

    if (iconOnly) {
        return noTooltip ? (
            <IconButton
                data-trackid="user_mailTo"
                variant="slim"
                href={`mailto:${user?.email}`}
                sx={{ color: useWhiteFont ? 'button.white' : 'button.primary' }}
            >
                <Icon src="Email" size={FontSizes[2]} />
            </IconButton>
        ) : (
            <DarkTooltip arrow title={user?.email}>
                <IconButton
                    data-trackid="user_mailTo"
                    variant="slim"
                    href={`mailto:${user?.email}`}
                    sx={{
                        color: useWhiteFont ? 'button.white' : 'button.primary'
                    }}
                >
                    <Icon src="Email" size={FontSizes[2]} />
                </IconButton>
            </DarkTooltip>
        );
    }

    return (
        <Box display="flex" alignItems="center" className={className}>
            <Typography>{user?.email}</Typography>
            <IconButton
                data-trackid="user_mailTo"
                variant="slim"
                href={`mailto:${user?.email}`}
                sx={{ color: useWhiteFont ? 'button.white' : 'button.primary' }}
            >
                <Icon src="OpenInNew" size={FontSizes[2]} />
            </IconButton>
        </Box>
    );
};

/**
 * A component that displays a user's first name.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.className] - CSS class name to apply to the component
 *
 * @returns {React.ReactElement} A span element containing the user's first name
 *
 * @example
 * <UserFirstName className="user-name" />
 */
export const UserFirstName = ({ className }) => {
    const { user } = useContext(UserContext);

    return <span className={className}>{user?.firstName}</span>;
};

/**
 * A component that displays a user's title.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.className] - CSS class name to apply to the component
 * @param {Object} [props.userOverride] - User object to override the user from context
 * @param {boolean} [props.useWhiteFont] - If true, uses white text color instead of default
 *
 * @returns {React.ReactElement} A Typography component displaying the user's title
 *
 * @example
 * <UserTitle
 *   className="title"
 *   userOverride={{
 *     title: "Manager",
 *     anonymous: false
 *   }}
 *   useWhiteFont={true}
 * />
 */
export const UserTitle = ({ className, userOverride, useWhiteFont }) => {
    const { user: userContext } = useContext(UserContext);
    const user = userOverride || userContext;

    const { t } = useTranslation();

    const { title } = user || {};

    const titleToShow =
        user?.anonymous || !user?.title ? t('Common.noTitleFound') : title;

    return (
        <Typography
            data-testid="userTitle"
            className={className}
            sx={{
                color: useWhiteFont ? 'text.white' : 'text.primary'
            }}
        >
            {titleToShow}
        </Typography>
    );
};

/**
 * A component that displays a user's avatar/icon image.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.className] - CSS class name to apply to the component
 * @param {boolean} [props.noLink] - If true, disables the link wrapper around the avatar
 * @param {Object} [props.userOverride] - User object to override the user from context
 *
 * @returns {React.ReactElement} A component displaying the user's avatar
 *
 * @example
 * <UserIcon
 *   userOverride={{
 *     firstName: "John",
 *     lastName: "Doe",
 *     image: "avatar.jpg"
 *   }}
 * />
 */
export const UserIcon = ({ noLink, userOverride }) => {
    const { user: userContext } = useContext(UserContext);
    const user = userOverride || userContext;
    const { clientId, partnerId } = useParams();

    if (user == null) {
        return null;
    }

    const { id } = user;

    const displayName = getFullName(user);

    if (!noLink) {
        const route = Route.buildBaseAdminRoute({ clientId, partnerId });

        return (
            <Link
                to={`${route}/user/${id}/overview`}
                data-trackid="userIconClassNoLinkLinkRender"
            >
                <StringAvatar sx={{ height: '24px', width: '24px' }}>
                    {displayName}
                </StringAvatar>
            </Link>
        );
    }

    return (
        <StringAvatar sx={{ height: '24px', width: '24px' }}>
            {displayName}
        </StringAvatar>
    );
};

const RenderUser = ({
    className,
    includeTitle,
    link = false,
    noTruncate = false,
    user,
    useWhiteFont
}) => {
    const { t } = useTranslation();
    const { anonymous, disabled, expired } = user || {};

    let hasTitle = true;
    if (anonymous) {
        hasTitle = false;
    }

    return (
        <Box
            display="flex"
            alignItems="center"
            data-testid="userIconAndNameClassBoxContainer"
            width="100%"
        >
            <Box flex={1}>
                {includeTitle ? (
                    <Box
                        display="flex"
                        flexDirection="column"
                        width="100%"
                        data-testid="userIconAndNameClassContainerWithIncludeTitle"
                    >
                        <Box
                            display="flex"
                            width="100%"
                            style={{ fontWeight: 600 }}
                        >
                            <UserFullName
                                className={className}
                                link={link}
                                noTruncate={noTruncate}
                                userOverride={user}
                                useWhiteFont={useWhiteFont}
                            />
                        </Box>
                        {hasTitle && (
                            <Box
                                display="flex"
                                width="100%"
                                data-testid="userIconAndNameClassContainerWithIncludeTitleTitle"
                            >
                                <UserTitle
                                    className="small"
                                    userOverride={user}
                                    useWhiteFont={useWhiteFont}
                                />
                            </Box>
                        )}
                    </Box>
                ) : (
                    <UserFullName
                        className={className}
                        data-testid="userFullNameNoIncludeTitle"
                        link={link}
                        noTruncate={noTruncate}
                        userOverride={user}
                        useWhiteFont={useWhiteFont}
                    />
                )}
            </Box>
            {disabled && (
                <DarkTooltip arrow title={t('User.disabled')}>
                    <Box>
                        <IconWithVariant
                            src="NoUserAccount"
                            size={FontSizes[2]}
                            wrapperProps={{ marginLeft: '8px' }}
                        />
                    </Box>
                </DarkTooltip>
            )}
            {expired && (
                <DarkTooltip arrow title={t('User.expired')}>
                    <Box>
                        <IconWithVariant
                            src="UserOff"
                            size={FontSizes[2]}
                            wrapperProps={{ marginLeft: '8px' }}
                        />
                    </Box>
                </DarkTooltip>
            )}
        </Box>
    );
};

/**
 * Resolves user from the context.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.className] - User object containing user information
 * @param {boolean} [props.includeTitle] - User object containing user information
 * @param {boolean} [props.link] - User object containing user information
 * @param {boolean} [props.userOverride] - User object containing user information
 * @param {boolean} [props.noTruncate] - User object containing user information
 * @param {boolean} [props.useWhiteFont] - User object containing user information
 *
 * @returns {React.ReactElement} A component displaying user icon and name
 *
 * @example
 * <UserFromContext
 *   userOverride={{
 *     firstName: "John",
 *     lastName: "Doe",
 *     email: "john.doe@example.com",
 *     avatar: "https://example.com/avatar.jpg"
 *   }}
 *   includeTitle
 * />
 */
export const UserFromContext = ({
    className,
    includeTitle,
    link,
    userOverride,
    noTruncate = false,
    useWhiteFont = false
}) => {
    const { user: userContext } = useContext(UserContext);
    const user = userOverride || userContext;

    if (user == null || user.name === undefined) {
        return null;
    }

    return (
        <RenderUser
            className={className}
            includeTitle={includeTitle}
            link={link}
            noTruncate={noTruncate}
            user={user}
            useWhiteFont={useWhiteFont}
        />
    );
};

/**
 * A component that displays a user's manager information.
 * Fetches and displays the manager's details using the user's managerPlatformUserId.
 * Shows a loading indicator while fetching and a "No manager found" message if no manager exists.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.className] - CSS class name to apply to the component
 * @param {boolean} [props.useWhiteFont] - If true, uses white text color instead of default
 *
 * @returns {React.ReactElement} A component displaying the user's manager information
 *
 * @example
 * <UserManager
 *   className="manager-info"
 *   useWhiteFont={true}
 * />
 */
export const UserManager = ({ className, useWhiteFont }) => {
    const { t } = useTranslation();
    const { clientId } = useParams();
    const { user } = useContext(UserContext);

    const { managerPlatformUserId } = user || {};

    const { user: manager, loading } = useFetchSingleUser({
        clientId,
        userId: managerPlatformUserId
    });

    if (managerPlatformUserId && loading) {
        return <CircularProgress size="13px" />;
    }

    if (manager) {
        return <UserFromContext userOverride={manager} />;
    }

    return (
        <Typography
            data-testid="userManagerEmpty"
            className={className}
            sx={{
                color: useWhiteFont ? 'text.white' : 'text.primary'
            }}
        >
            {t('Common.noManagerFound')}
        </Typography>
    );
};
